import classNames from "classnames";
import * as React from "react";

import styles from "./SidebarResizeHandle.module.css";

interface SidebarResizeHandleProps {
  /**
   * Widens (positive) or narrows (negative) the sidebar by that many pixels.
   *
   * A *delta*, not a width, because the handle never knows what the column
   * currently measures: the owner reads that off the laid-out element, which is
   * what keeps the CSS `min-width` / `max-width` authoritative — see
   * {@link PreviewStep}.
   */
  onResize: (deltaX: number) => void;
}

/**
 * How far one arrow key moves the edge.
 *
 * Big enough that a keyboard user reaches a useful width in a few presses
 * (16px a press is 16 presses across the sidebar's whole range) and small
 * enough to land on a particular width rather than overshoot it.
 */
const KEYBOARD_STEP_PX = 16;

/**
 * The drag handle between the preview's sidebar and its viewport.
 *
 * Follows the app's existing resize handles — `AppNavigation/Sidebar` and
 * `WorkspaceResizeHandle`, which are the same component twice — down to the
 * accessible name, the `separator` role and the pointer-capture dance: capture
 * on pointer-down so the drag survives the pointer leaving this 8px strip, and
 * a `user-select` lock on the document so dragging over the joint list does not
 * select its text.
 *
 * Two things are **not** copied from those two:
 *
 * - **They are positioned absolutely inside the panel they resize.** That works
 *   for a panel that does not scroll, and would not work here: this sidebar is
 *   the preview's single scroll region, so a handle inside it would scroll up
 *   out of reach with the joint list and be clipped by the same `overflow`.
 *   This one is an ordinary flex item in the gap between the two panes instead.
 * - **Neither is reachable by keyboard.** They are un-focusable `<div>`s, so the
 *   only way to resize is a pointer drag — which is a gap rather than a
 *   convention to preserve, and is why arrow keys are handled here.
 *
 * No `aria-valuenow`: the owner keeps the width in the DOM rather than in React
 * state (a re-render per pointer move would re-render every joint row), so
 * there is no width in hand to report. The label and role still announce what
 * this is, and the arrow keys still work; a value that lied would be worse than
 * one that is absent.
 */
export function SidebarResizeHandle({ onResize }: SidebarResizeHandleProps) {
  const currentX = React.useRef(0);
  const resizeHandleRef = React.useRef<HTMLDivElement>(null);

  const [isDragging, setIsDragging] = React.useState(false);

  const handlePointerDown = React.useCallback((event: React.PointerEvent) => {
    setIsDragging(true);
    currentX.current = event.clientX;

    // Forward all pointer events to this element
    resizeHandleRef.current?.setPointerCapture(event.pointerId);
    document.documentElement.classList.add(styles.disableUserInteraction);
  }, []);

  const handlePointerMove = React.useCallback(
    (event: React.PointerEvent) => {
      const deltaX = event.clientX - currentX.current;
      onResize(deltaX);

      currentX.current = event.clientX;
    },
    [onResize],
  );

  const handlePointerUp = React.useCallback((event: React.PointerEvent) => {
    setIsDragging(false);
    document.documentElement.classList.remove(styles.disableUserInteraction);
    resizeHandleRef.current?.releasePointerCapture(event.pointerId);
  }, []);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
        return;
      }
      // Otherwise the arrow scrolls the dialog while the column resizes.
      event.preventDefault();
      onResize(
        event.key === "ArrowLeft" ? -KEYBOARD_STEP_PX : KEYBOARD_STEP_PX,
      );
    },
    [onResize],
  );

  return (
    <div
      aria-label="Resize sidebar width"
      aria-orientation="vertical"
      className={classNames(styles.resizeHandle, {
        [styles.active]: isDragging,
      })}
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      onPointerMove={isDragging ? handlePointerMove : undefined}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      ref={resizeHandleRef}
      role="separator"
      tabIndex={0}
    >
      <div className={styles.resizeHandleIndicator} />
    </div>
  );
}
